import i18n from '@/locales/config';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App, ConfigProvider, ConfigProviderProps, theme } from 'antd';
import pt_BR from 'antd/lib/locale/pt_BR';
import deDE from 'antd/locale/de_DE';
import enUS from 'antd/locale/en_US';
import vi_VN from 'antd/locale/vi_VN';
import zhCN from 'antd/locale/zh_CN';
import zh_HK from 'antd/locale/zh_HK';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import weekday from 'dayjs/plugin/weekday';
import React, { ReactNode, useEffect, useState } from 'react';
import { history } from 'umi';
import { ThemeProvider, useTheme } from './components/theme-provider';
import { TooltipProvider } from './components/ui/tooltip';
import { ANTD_THEME_TOKENS } from './theme/tokens';
import storage from './utils/authorization-util';
import {
  loadFeatureFlags,
  notifyFeatureOff,
  routeBlockedBy,
} from './utils/feature-gates';
import { resumeSilentRenew } from './utils/oidc-manager';
import { loadReportAccess, reportOnlyRedirect } from './utils/report-access';
// Import our custom notification utility
import './utils/notification-util';
// Import message position override component

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

const AntLanguageMap = {
  en: enUS,
  zh: zhCN,
  'zh-TRADITIONAL': zh_HK,
  vi: vi_VN,
  'pt-BR': pt_BR,
  de: deDE,
};

const queryClient = new QueryClient();

type Locale = ConfigProviderProps['locale'];

function Root({ children }: React.PropsWithChildren) {
  const { resolvedTheme } = useTheme();
  const getLocale = (lng: string) =>
    AntLanguageMap[lng as keyof typeof AntLanguageMap] ?? enUS;

  const [locale, setLocal] = useState<Locale>(getLocale(storage.getLanguage()));

  i18n.on('languageChanged', function (lng: string) {
    storage.setLanguage(lng);
    setLocal(getLocale(lng));
  });

  // Bridge the active design-system theme (src/theme/tokens.ts) into antd so
  // legacy Ant Design screens follow theme switches.
  const bridge = ANTD_THEME_TOKENS[resolvedTheme];

  return (
    <>
      <ConfigProvider
        theme={{
          token: {
            fontFamily: 'Inter',
            ...bridge.token,
            borderRadiusLG: 16,
          },
          components: {
            Modal: {
              titleFontSize: 16,
              titleLineHeight: 1.4,
            },
          },
          algorithm: bridge.dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        }}
        locale={locale}
      >
        <App>{children}</App>
      </ConfigProvider>
      {/* <ReactQueryDevtools buttonPosition={'top-left'} /> */}
    </>
  );
}

const RootProvider = ({ children }: React.PropsWithChildren) => {
  useEffect(() => {
    // Because the language is saved in the backend, a token is required to obtain the api. However, the login page cannot obtain the language through the getUserInfo api, so the language needs to be saved in localstorage.
    const lng = storage.getLanguage();
    if (lng) {
      i18n.changeLanguage(lng);
    }
    // Google API scripts are now loaded lazily when needed. See useGoogleDrive hook.

    // Every full page load lands here, and this is the only thing that arms OIDC
    // token renewal — the sign-in paths are the only other code that builds a
    // UserManager, so without this a reloaded tab kept a session only until its
    // access token expired. No-ops when nobody is signed in.
    resumeSilentRenew();
  }, []);

  return (
    <TooltipProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="light" storageKey="ragflow-ui-theme">
          <Root>{children}</Root>
        </ThemeProvider>
      </QueryClientProvider>
    </TooltipProvider>
  );
};
export function rootContainer(container: ReactNode) {
  return <RootProvider>{container}</RootProvider>;
}

// Catch-all for routes outside the main layout, which wrappers/feature-gate does not wrap.
export function onRouteChange({
  location,
}: {
  location: { pathname: string };
}) {
  loadFeatureFlags().then((flags) => {
    // The flags may arrive after the user has moved on; only redirect from the route that was checked.
    const blockedBy = routeBlockedBy(flags, location.pathname);
    if (history.location.pathname === location.pathname && blockedBy) {
      notifyFeatureOff(blockedBy);
      history.replace('/home');
    }
  });
  // Covers the layouts/next routes, which have no wrapper. A failed read resolves reportOnly false.
  loadReportAccess().then((access) => {
    const target = reportOnlyRedirect(access, location.pathname);
    if (target && history.location.pathname === location.pathname) {
      history.replace(target);
    }
  });
}
